import { useCallback, useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Loader2, UserPlus, Search, ChevronLeft, ChevronRight, Wand2, Copy, UserCog, Trash2,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { IMPERSONATOR_NAME_KEY, setSession } from '@/lib/session';
import { generatePassword } from '@/lib/password';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  isSuperAdmin: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceRole: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface WorkspaceResult {
  id: string;
  name: string;
  ownerEmail: string | null;
  subscriptionStatus: string;
  planKey: string | null;
}

// Assignable workspace roles. 'owner' is excluded — ownership is implicit.
const ROLES = ['admin', 'finance', 'content', 'media', 'viewer'] as const;

export default function AdminUsers() {
  const { data: me } = useCurrentUser();
  const myId = me?.user?.id;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Create-account dialog state
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('viewer');
  const [creating, setCreating] = useState(false);

  // Workspace picker state (debounced search, like the Plans page).
  const [wsSearch, setWsSearch] = useState('');
  const [wsResults, setWsResults] = useState<WorkspaceResult[]>([]);
  const [wsSearching, setWsSearching] = useState(false);
  const [selectedWs, setSelectedWs] = useState<WorkspaceResult | null>(null);

  // Delete-account state
  const [selectedForDelete, setSelectedForDelete] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (search) params.set('search', search);
      const data = await apiFetch<{ users: AdminUser[]; pagination: Pagination }>(`/users?${params}`);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when the search term changes.
  useEffect(() => { setPage(1); }, [search]);

  // Debounced workspace search while the create dialog is open.
  useEffect(() => {
    if (!open) return;
    const q = wsSearch.trim();
    setWsSearching(true);
    const handle = setTimeout(() => {
      apiFetch<{ workspaces: WorkspaceResult[] }>(`/admin/workspaces?search=${encodeURIComponent(q)}`)
        .then((r) => setWsResults(r.workspaces || []))
        .catch((e) => toast.error(e instanceof Error ? e.message : 'Workspace search failed'))
        .finally(() => setWsSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [wsSearch, open]);

  // Clear the dialog's form whenever it closes.
  useEffect(() => {
    if (open) return;
    setName(''); setEmail(''); setPassword(''); setRole('viewer');
    setWsSearch(''); setWsResults([]); setSelectedWs(null);
  }, [open]);

  const handleGenerate = () => {
    setPassword(generatePassword());
    toast.success('Strong password generated');
  };

  const handleCopy = async () => {
    if (!password) {
      toast.error('Generate or enter a password first');
      return;
    }
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Failed to copy password');
    }
  };

  const handleCreate = async () => {
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!selectedWs) {
      toast.error('Please select a workspace');
      return;
    }
    try {
      setCreating(true);
      const res = await apiFetch<{ emailSent?: boolean }>('/users', {
        method: 'POST',
        body: JSON.stringify({ name, email, password, role, workspaceId: selectedWs.id }),
      });
      toast.success(
        res.emailSent
          ? `Account created for ${name} in ${selectedWs.name} — invitation email sent`
          : `Account created for ${name} in ${selectedWs.name} — but the invitation email failed to send`,
      );
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const toggleSuperAdmin = async (user: AdminUser, next: boolean) => {
    try {
      setTogglingId(user.id);
      await apiFetch(`/users/${user.id}`, { method: 'PATCH', body: JSON.stringify({ isSuperAdmin: next }) });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, isSuperAdmin: next } : u)));
      toast.success(next ? `${user.name} is now a super admin` : `Removed super admin from ${user.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update super admin');
    } finally {
      setTogglingId(null);
    }
  };

  const impersonate = async (user: AdminUser) => {
    if (!window.confirm(`Log in as ${user.name}? You'll be signed in as this user until you return to your own account.`)) return;
    try {
      const data = await apiFetch<{ token: string; user: { name: string }; workspaceId?: string | null }>(
        `/auth/impersonate/${user.id}`, { method: 'POST' });
      // The server parked the admin's own session in an httpOnly cookie for the banner's "Return"
      // to resume. All this browser keeps is the admin's name, to show in the banner — the first
      // admin's, if this is a hop from one impersonated account to another.
      const impersonatorName = localStorage.getItem(IMPERSONATOR_NAME_KEY)
        || localStorage.getItem('userName')
        || 'admin';
      // Swap to the target's session and hard-reload so all state re-resolves as them.
      setSession({ token: data.token, userName: data.user.name, workspaceId: data.workspaceId, impersonatorName });
      window.location.href = '/app';
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to impersonate user');
    }
  };

  const handleDelete = async () => {
    if (!selectedForDelete) return;
    setDeleting(true);
    try {
      await apiFetch(`/users/${selectedForDelete.id}`, { method: 'DELETE' });
      toast.success(`${selectedForDelete.name} has been deleted`);
      if (users.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        load();
      }
      setSelectedForDelete(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container mx-auto max-w-6xl py-10 px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Users</h1>
            <p className="text-sm text-muted-foreground">Create platform accounts and manage super admins.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button><UserPlus className="h-4 w-4 mr-2" />New account</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New account</DialogTitle>
                <DialogDescription>
                  Creates a platform account, adds it to the selected workspace with the chosen role, and sends the user an invitation email with their temporary password.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="new-name">Name</Label>
                  <Input id="new-name" value={name} placeholder="John Doe" onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-email">Email</Label>
                  <Input id="new-email" type="email" value={email} placeholder="user@example.com" onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Password</Label>
                  <Input id="new-password" type="text" value={password}
                    placeholder="Min 8 chars, upper+lower+number+special"
                    onChange={(e) => setPassword(e.target.value)} />
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" className="flex-1" onClick={handleGenerate}>
                      <Wand2 className="h-4 w-4 mr-2" />Generate strong password
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!password}>
                      <Copy className="h-4 w-4 mr-2" />Copy
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-role">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger id="new-role">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Workspace</Label>
                  <Input
                    value={wsSearch}
                    placeholder="Search by name or domain…"
                    onChange={(e) => { setWsSearch(e.target.value); setSelectedWs(null); }}
                  />
                  <div className="max-h-44 overflow-y-auto rounded-md border divide-y">
                    {wsSearching ? (
                      <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : wsResults.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-6 text-center">No workspaces found.</p>
                    ) : (
                      wsResults.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => setSelectedWs(w)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selectedWs?.id === w.id ? 'bg-muted' : ''}`}
                        >
                          <div className="font-medium">{w.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {w.ownerEmail || 'no owner'} · {w.subscriptionStatus}{w.planKey ? ` · ${w.planKey}` : ''}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
                <Button onClick={handleCreate} className="w-full" disabled={creating || !selectedWs}>
                  {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</>) : 'Create account'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Workspace</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Super admin</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    {search ? 'No users match your search' : 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const isSelf = user.id === myId;
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.name}
                        {isSelf && <Badge variant="outline" className="ml-2">You</Badge>}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.workspaceName
                          ? user.workspaceName
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {user.workspaceRole
                          ? <Badge variant="outline" className="capitalize">{user.workspaceRole}</Badge>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.isSuperAdmin}
                            disabled={isSelf || togglingId === user.id}
                            onCheckedChange={(v) => toggleSuperAdmin(user, v)}
                            title={isSelf ? "You can't change your own super-admin status" : undefined}
                          />
                          {user.isSuperAdmin && <Badge variant="secondary">Super admin</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={isSelf}
                          onClick={() => impersonate(user)}
                          title={isSelf ? "You can't impersonate yourself" : 'Log in as this user'}
                        >
                          <UserCog className="h-4 w-4 mr-1" />Impersonate
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={isSelf}
                          onClick={() => setSelectedForDelete(user)}
                          title={isSelf ? "You can't delete yourself" : 'Delete this user'}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />Previous
              </Button>
              <span className="text-sm px-2">{pagination.page} / {pagination.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Delete User Confirmation */}
        <AlertDialog
          open={!!selectedForDelete}
          onOpenChange={(o) => { if (!o) setSelectedForDelete(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete user?</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedForDelete && !selectedForDelete.lastLoginAt
                  ? <>Permanently delete the pending invite for <span className="font-medium text-foreground">{selectedForDelete.email}</span>? The account has never been used, so it will be removed entirely and its email address freed for re-use.</>
                  : <>Delete <span className="font-medium text-foreground">{selectedForDelete?.name}</span> ({selectedForDelete?.email})? Their account will be deactivated and their email address released for re-use. This cannot be undone.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => { e.preventDefault(); handleDelete(); }}
                disabled={deleting}
              >
                {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
